import React, { useState } from 'react';
import { PenLine, CheckCircle, ArrowLeft, Users, Heart, Building2, GraduationCap, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PetitionSign = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    zip_code: '',
    comment: '',
    is_dc_resident: true,
    share_publicly: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/petition/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success('Thank you for signing the petition!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to sign petition');
      }
    } catch (error) {
      toast.error('Failed to sign petition. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main id="main-content" tabIndex={-1} className="py-20">
          <div className="max-w-xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <div className="w-20 h-20 bg-[#079897]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#079897]" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A2037] mb-4">Thank You for Signing!</h1>
              <p className="text-slate-600 mb-6">
                Your voice matters. Together, we can protect and strengthen DC's early childhood education system 
                for our children, families, and educators.
              </p>
              <div className="bg-[#079897]/10 rounded-xl p-4 mb-6">
                <p className="text-[#079897] font-medium">
                  "Investing in early childhood educators is investing in DC's future."
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button className="bg-[#079897] hover:bg-[#067a79]">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Return to Homepage
                  </Button>
                </Link>
                <Button 
                  variant="outline"
                  onClick={() => {
                    navigator.share?.({
                      title: 'Sign the Petition to Protect Early Childhood Education in DC',
                      url: window.location.href
                    }) || navigator.clipboard.writeText(window.location.href).then(() => toast.success('Link copied!'));
                  }}
                >
                  Share This Petition
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center text-[#079897] hover:text-[#067a79] mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Homepage
          </Link>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Petition Content - Left Side */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#079897] to-[#0A2037] p-6 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <PenLine className="w-6 h-6" />
                    </div>
                    <span className="text-white/80 text-sm font-medium uppercase tracking-wider">Petition</span>
                  </div>
                  <h1 className="text-2xl font-bold">
                    Sign the Petition: Protect Early Childhood Education in DC
                  </h1>
                </div>

                {/* Petition Body */}
                <div className="p-6">
                  {/* Intro */}
                  <div className="mb-6 text-slate-700 leading-relaxed">
                    <p className="mb-4">
                      DC's early childhood educators and families are under pressure. Critical programs that make quality, 
                      affordable early learning possible — including the <strong>Early Childhood Educator Pay Equity Fund</strong> and 
                      the <strong>Child Care Subsidy Program</strong> — are at risk.
                    </p>
                    <p className="mb-4">
                      These programs have lifted wages, expanded health coverage, and improved access to care for thousands 
                      of children and educators. <strong className="text-[#079897]">The District must not turn back now.</strong>
                    </p>
                    <p>
                      Join fellow residents in urging Mayor Bowser and Chairman Mendelson to protect and strengthen these 
                      vital investments in DC's children, families, and workforce.
                    </p>
                  </div>

                  {/* Letter */}
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <p className="text-sm text-slate-500 uppercase tracking-wider mb-3 font-semibold">The Petition</p>
                    
                    <div className="text-slate-700 text-sm leading-relaxed space-y-3">
                      <p className="font-semibold">To:</p>
                      <p className="pl-4">
                        The Honorable Muriel Bowser<br />
                        <span className="text-slate-500">Mayor, District of Columbia</span>
                      </p>
                      <p className="pl-4">
                        The Honorable Phil Mendelson<br />
                        <span className="text-slate-500">Chairman, Council of the District of Columbia</span>
                      </p>

                      <p className="pt-3">Dear Mayor Bowser and Chairman Mendelson,</p>

                      <p>
                        We, the residents of the District of Columbia, urge our city leaders to safeguard the essential 
                        early learning investments that children, families, and educators rely on every day.
                      </p>

                      <p>
                        The Early Childhood Educator Pay Equity Fund has raised wages for thousands of early educators 
                        across hundreds of child development centers, while HealthCare4ChildCare has connected many to 
                        affordable health coverage for the first time. Meanwhile, the Child Care Subsidy Program helps 
                        more than 7,500 children access care, though current reimbursement rates remain below the true 
                        cost of providing it.
                      </p>

                      <p>
                        Despite this progress, DC's early learning system remains fragile. A waiting list for new Pay 
                        Equity Fund providers has been in place since 2025, and continuing budget shortfalls threaten 
                        both subsidy access and workforce stability. Weakening these programs would jeopardize family 
                        stability, force educators from the field, and undercut the foundations of our economy.
                      </p>

                      <p className="font-semibold pt-2">We call on District leaders to:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li>
                          <strong>Fully fund the Early Childhood Educator Pay Equity Fund</strong> to ensure fair pay 
                          and retention of qualified educators.
                        </li>
                        <li>
                          <strong>Protect and strengthen the Child Care Subsidy Program</strong> by maintaining rates 
                          that reflect actual enrollment and the real cost of care.
                        </li>
                        <li>
                          <strong>Eliminate barriers to access</strong>, including program and subsidy waitlists.
                        </li>
                        <li>
                          <strong>Recognize early childhood education as essential infrastructure</strong> that supports 
                          DC's children, working families, and economic future.
                        </li>
                      </ul>

                      <p className="pt-3 font-semibold text-[#079897]">
                        Investing in early childhood educators is investing in DC's future.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sign Form - Right Side */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-6">
                <h2 className="text-xl font-bold text-[#0A2037] mb-4">Add Your Name</h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="first_name">First Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        type="text"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="First name"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        type="text"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Last name"
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      type="text"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder="e.g., 20001"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="comment">Add a Personal Comment (Optional)</Label>
                    <Textarea
                      id="comment"
                      name="comment"
                      value={formData.comment}
                      onChange={handleChange}
                      placeholder="Why is this issue important to you?"
                      className="mt-1"
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_dc_resident"
                        checked={formData.is_dc_resident}
                        onChange={handleChange}
                        className="mt-1 rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-600">I am a DC resident</span>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="share_publicly"
                        checked={formData.share_publicly}
                        onChange={handleChange}
                        className="mt-1 rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-600">Display my name publicly with this petition</span>
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#079897] hover:bg-[#067a79] py-6 text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      'Signing...'
                    ) : (
                      <>
                        <PenLine className="w-5 h-5 mr-2" />
                        Sign the Petition
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-slate-500 text-center mt-4">
                  By signing, you agree to receive email updates from DCELC. You can unsubscribe at any time.
                </p>

                {/* Stats */}
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center justify-center gap-2 text-slate-600">
                    <Users className="w-5 h-5 text-[#079897]" />
                    <span className="text-sm">Join others who have signed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why This Matters */}
          <div className="mt-12 bg-gradient-to-br from-[#0A2037] to-[#152d4a] rounded-2xl p-8 text-white">
            <h2 className="text-xl font-bold mb-6 text-center">Why This Matters</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <GraduationCap className="w-6 h-6 text-[#95C73E]" />
                </div>
                <p className="text-sm text-white/90">Thousands of educators have received wage increases through the Pay Equity Fund</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Heart className="w-6 h-6 text-[#95C73E]" />
                </div>
                <p className="text-sm text-white/90">7,500+ children access care through the Child Care Subsidy Program</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-[#95C73E]" />
                </div>
                <p className="text-sm text-white/90">HealthCare4ChildCare provides affordable coverage for educators</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Building2 className="w-6 h-6 text-[#95C73E]" />
                </div>
                <p className="text-sm text-white/90">Hundreds of child development centers participate in these programs</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PetitionSign;
